/**
 * usePersistedState — namespaced, reactive localStorage with logout cleanup.
 *
 * Background (architecture review §5): 24 files write to `localStorage`
 * independently with no namespace convention or eviction strategy.  This
 * composable centralises the pattern under a `pf_` prefix so:
 *   - We can wipe everything on logout via `clearAllPersistedState()`.
 *   - Key collisions across features are structurally impossible.
 *   - Reactive consumers don't need to reimplement read-on-mount + write-on-change.
 *
 * Usage:
 *   const recentSearches = usePersistedState('recent-searches', [])
 *   recentSearches.value.push('foo')   // auto-persisted under `pf_recent-searches`
 *
 * Logout integration: `clearAllPersistedState()` is called from the auth
 * store's `logoutUser` action.  Auth tokens (sessionStorage / refreshToken)
 * are intentionally not under this namespace — they have separate lifecycle
 * rules and `auth.js` clears them explicitly.
 */

import { ref, watch } from 'vue';

export const PERSISTED_STATE_NAMESPACE = 'pf_';

function isStorageAvailable() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function readPersisted(key, defaultValue) {
  if (!isStorageAvailable()) return defaultValue;
  try {
    const raw = window.localStorage.getItem(PERSISTED_STATE_NAMESPACE + key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}

function writePersisted(key, value) {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(PERSISTED_STATE_NAMESPACE + key, JSON.stringify(value));
  } catch {
    // quota exceeded / private-mode storage refusal — silently drop.
  }
}

/**
 * Reactive ref synced to namespaced localStorage.
 *
 * @template T
 * @param {string} key       Storage suffix; the final key is `pf_<key>`.
 * @param {T} defaultValue   Returned when nothing is stored or parsing fails.
 * @returns {import('vue').Ref<T>}
 */
export function usePersistedState(key, defaultValue) {
  const state = ref(readPersisted(key, defaultValue));
  watch(
    state,
    (next) => {
      writePersisted(key, next);
    },
    { deep: true }
  );
  return state;
}

/**
 * Remove every `pf_*` key from localStorage.  Call this on logout to ensure
 * persisted UI state doesn't leak between users on shared machines.
 */
export function clearAllPersistedState() {
  if (!isStorageAvailable()) return;
  const keys = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(PERSISTED_STATE_NAMESPACE)) keys.push(k);
  }
  keys.forEach((k) => window.localStorage.removeItem(k));
}
